import { InlineNotification } from '@carbon/react';
import { useAtom } from 'jotai';
import { Switch, useLocation } from 'react-router-dom';
import { Route } from 'components/route';
import routes from '~/routes';
import { DocumentHeader } from '../common/header';
import { useDocumentAtom } from '../common/atoms';
import { DocumentOverviewPage } from '../overview/page';
import { InterpretationsPage } from '../interpretations/page';

// Single entry point for a document and its tabs.
//
// The tabs live at URLs that don't share a prefix — `/courthouse_documents/:id`
// (and the other typed variants) for Overview, `/documents/:id/interpretations`
// for Interpretation — so this route matches all of them and branches inside.
// Fetching here means a tab switch swaps only the panel below a persistent
// header, instead of unmounting the page and refetching the document.
//
// The fetch is keyed on `/documents/:id` regardless of which URL the user came
// in on, so both tabs share one cache entry. `documents#show` 302s to the typed
// path, so this resolves to the same payload either way.
const DocumentEntrypointPage = () => {
  const location = useLocation();
  const [, id] = location.pathname.split('/').filter((el) => el);
  const documentAtom = useDocumentAtom(`/documents/${id}`);
  const [document, getDocument] = useAtom(documentAtom);

  if (document.data && 'error' in document.data) {
    return (
      <InlineNotification
        kind="error"
        title={document.data.error}
        lowContrast
      />
    );
  }
  if (!document.data) return null;

  return (
    <DocumentHeader document={document.data} getDocument={getDocument}>
      <Switch>
        <Route path={routes.documentInterpretationsPath}>
          <InterpretationsPage
            document={document.data}
            getDocument={getDocument}
          />
        </Route>
        <Route>
          <DocumentOverviewPage
            document={document.data}
            getDocument={getDocument}
          />
        </Route>
      </Switch>
    </DocumentHeader>
  );
};

export { DocumentEntrypointPage };
